import React, { useState } from 'react';
import { Issue, Status } from '../types';
import { STATUSES, USERS, LABELS } from '../store';
import { StatusIcon, PriorityIcon } from './icons';
import { format } from 'date-fns';
import { Plus, MoreHorizontal } from 'lucide-react';
interface BoardViewProps {
  issues: Issue[];
  onIssueClick: (issue: Issue) => void;
  onUpdateIssueStatus: (id: string, status: Status) => void;
}
export function BoardView({
  issues,
  onIssueClick,
  onUpdateIssueStatus
}: BoardViewProps) {
  return <div className="flex-1 overflow-x-auto overflow-y-hidden bg-white p-6 flex space-x-4" data-id="element-1">
      {STATUSES.map(status => <BoardColumn key={status.id} status={status} issues={issues.filter(i => i.status === status.id)} onIssueClick={onIssueClick} onUpdateIssueStatus={onUpdateIssueStatus} data-id="element-2" />)}
    </div>;
}
interface BoardColumnProps {
  status: {
    id: Status;
    label: string;
  };
  issues: Issue[];
  onIssueClick: (issue: Issue) => void;
  onUpdateIssueStatus: (id: string, status: Status) => void;
}
function BoardColumn({
  status,
  issues,
  onIssueClick,
  onUpdateIssueStatus
}: BoardColumnProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => {
    setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const issueId = e.dataTransfer.getData('text/plain');
    if (issueId) {
      onUpdateIssueStatus(issueId, status.id);
    }
  };
  return <div className={`flex flex-col w-[320px] flex-shrink-0 rounded-lg transition-colors ${isDragOver ? 'bg-slate-50/50' : ''}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} data-id="element-3">
      <div className="flex items-center justify-between mb-3 px-1 group" data-id="element-4">
        <div className="flex items-center text-[13px] font-medium text-slate-700" data-id="element-5">
          <StatusIcon status={status.id} className="w-4 h-4 mr-2" data-id="element-6" />
          {status.label}
          <span className="ml-2 text-slate-400 font-normal" data-id="element-7">
            {issues.length}
          </span>
        </div>
        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8">
          <button className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors" data-id="element-9">
            <Plus className="w-3.5 h-3.5" data-id="element-10" />
          </button>
          <button className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors ml-0.5" data-id="element-11">
            <MoreHorizontal className="w-3.5 h-3.5" data-id="element-12" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-2.5 pb-4 px-1" data-id="element-13">
        {issues.map(issue => <IssueCard key={issue.id} issue={issue} onClick={() => onIssueClick(issue)} data-id="element-14" />)}
        {issues.length === 0 && <div className="h-24 border border-dashed border-slate-200 rounded-lg flex items-center justify-center text-[13px] text-slate-400 bg-slate-50/50" data-id="element-15">
            Drop issues here
          </div>}
      </div>
    </div>;
}
function IssueCard({
  issue,
  onClick
}: {
  issue: Issue;
  onClick: () => void;
}) {
  const assignee = USERS.find(u => u.id === issue.assigneeId);
  const labels = issue.labelIds.map(id => LABELS.find(l => l.id === id)).filter(Boolean);
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', issue.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  return <div draggable onDragStart={handleDragStart} onClick={onClick} className="bg-white p-3.5 rounded-lg border border-[#eef0f2] shadow-linear hover:shadow-linear-hover cursor-pointer transition-all group" data-id="element-16">
      <div className="flex items-start justify-between mb-2" data-id="element-17">
        <span className="text-[12px] font-medium text-slate-500 group-hover:text-slate-700 transition-colors" data-id="element-18">
          {issue.identifier}
        </span>
        {assignee && <img src={assignee.avatar} alt={assignee.name} className="w-4 h-4 rounded-full border border-slate-200" title={assignee.name} data-id="element-19" />}
      </div>

      <h3 className="text-[13px] font-medium text-slate-900 mb-3.5 leading-snug line-clamp-2" data-id="element-20">
        {issue.title}
      </h3>

      <div className="flex items-center space-x-2" data-id="element-21">
        <PriorityIcon priority={issue.priority} className="w-3.5 h-3.5" data-id="element-22" />

        {labels.length > 0 && <div className="flex flex-wrap gap-1.5" data-id="element-23">
            {labels.map(label => <div key={label?.id} className="flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium border border-slate-200/60 text-slate-600 bg-slate-50/50" data-id="element-24">
                <div className="w-1.5 h-1.5 rounded-full mr-1.5" style={{
            backgroundColor: label?.color
          }} data-id="element-25" />
                {label?.name}
              </div>)}
          </div>}
      </div>
    </div>;
}